import { z } from 'zod';

const decimal = z.string().max(78).regex(/^(0|[1-9][0-9]*)$/)
  .pipe(z.string().refine(value => BigInt(value) < (1n << 256n)));
const positiveDecimal = decimal.refine(value => BigInt(value) > 0n);
const hash = z.string().regex(/^0x[0-9a-f]{64}$/i).transform(value => value.toLowerCase() as `0x${string}`);
const index = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const leg = z.strictObject({ orderId: decimal, legIndex: z.union([z.literal(0), z.literal(1)]) });
const noAuthority = {
  executionAuthorized: z.literal(false), signingAuthorized: z.literal(false), sendAuthorized: z.literal(false),
  resubmissionAuthorized: z.literal(false), nonceReleaseAuthorized: z.literal(false), accountingEligible: z.literal(false),
};

// These raw ASCII strings are also SQLite BINARY keyset positions. Do not
// normalize the timestamp precision or UUID case while reading/returning them.
export const conditionalMultiMarketOrderHistoryCursorSchema = z.strictObject({
  admittedAt: z.iso.datetime(), attemptId: z.uuid(),
});
export type ConditionalMultiMarketOrderHistoryCursor = z.infer<typeof conditionalMultiMarketOrderHistoryCursorSchema>;

const fillSchema = z.strictObject({
  orderId: decimal, legIndex: z.union([z.literal(0), z.literal(1)]), globalEpoch: decimal, marketId: hash,
  marketRevision: decimal, logIndex: index, revision: positiveDecimal,
  actualInput: positiveDecimal, actualOutput: positiveDecimal, cumulativeInput: positiveDecimal,
  cumulativeOutput: positiveDecimal, complete: z.boolean(), sharedRemainingAfter: decimal.nullable(),
});
const receiptSchema = z.strictObject({
  revision: index.refine(value => value > 0), observationId: hash, recordedAt: z.iso.datetime(),
  outcome: z.enum(['success', 'reverted']), transactionHash: hash, blockNumber: decimal, blockHash: hash,
  transactionIndex: index, headBlockNumber: decimal, headBlockHash: hash,
  requiredConfirmations: z.number().int().min(2).max(256), gasUsed: positiveDecimal, nativeGasCost: decimal,
  fill: fillSchema.nullable(),
});

/** A strict minimal display projection, NOT full receipt/attempt proof, current
 * chain state, execution authority, or accounting evidence. The server restores
 * the complete stored codecs; the browser additionally binds the selected
 * registration/session and original chain-assigned order IDs. */
export const conditionalMultiMarketOrderHistoryItemSchema = z.strictObject({
  attemptId: z.uuid(), admittedAt: z.iso.datetime(), selected: leg, peer: leg.nullable(),
  transactionHash: hash, maximumThisFill: positiveDecimal,
  dispatch: z.strictObject({
    state: z.enum(['held', 'claimed']), claimedAt: z.iso.datetime().nullable(),
    transportReports: z.array(z.strictObject({
      outcome: z.enum(['accepted', 'unknown', 'not-broadcast']), reportedAt: z.iso.datetime(),
    })).max(3),
  }),
  receipt: receiptSchema.nullable(), quarantined: z.boolean(),
  state: z.enum(['held', 'claimed-unresolved', 'recorded-fill', 'recorded-revert', 'quarantined']),
  ...noAuthority,
}).transform((item, ctx) => {
  const { dispatch, receipt, peer, selected } = item;
  const expectedState = item.quarantined ? 'quarantined' : receipt?.outcome === 'success' ? 'recorded-fill'
    : receipt?.outcome === 'reverted' ? 'recorded-revert' : dispatch.state === 'held' ? 'held' : 'claimed-unresolved';
  const invalid = item.state !== expectedState
    || (peer ? peer.orderId === selected.orderId || peer.legIndex === selected.legIndex : selected.legIndex !== 0)
    || (dispatch.state === 'held') !== (dispatch.claimedAt === null)
    || dispatch.state === 'held' && (dispatch.transportReports.length !== 0 || receipt !== null)
    || new Set(dispatch.transportReports.map(report => report.outcome)).size !== dispatch.transportReports.length
    || receipt !== null && (receipt.transactionHash !== item.transactionHash
      || (receipt.outcome === 'success') !== (receipt.fill !== null)
      || BigInt(receipt.headBlockNumber) - BigInt(receipt.blockNumber) + 1n < BigInt(receipt.requiredConfirmations)
      || BigInt(receipt.nativeGasCost) % BigInt(receipt.gasUsed) !== 0n)
    || receipt?.fill != null && (receipt.fill.orderId !== selected.orderId || receipt.fill.legIndex !== selected.legIndex
      || BigInt(receipt.fill.actualInput) > BigInt(item.maximumThisFill)
      || BigInt(receipt.fill.cumulativeInput) < BigInt(receipt.fill.actualInput)
      || BigInt(receipt.fill.cumulativeOutput) < BigInt(receipt.fill.actualOutput)
      || (receipt.fill.sharedRemainingAfter === null) !== (peer === null));
  if (invalid) ctx.addIssue({ code: 'custom', message: 'Inconsistent conditional order history' });
  return item;
});
export type ConditionalMultiMarketOrderHistoryItem = z.infer<typeof conditionalMultiMarketOrderHistoryItemSchema>;

export const conditionalMultiMarketOrderHistoryPageSchema = z.strictObject({
  schemaVersion: z.literal(1), version: z.literal('conditional-multimarket.2'), chainId: z.literal(4663),
  userId: z.string().min(1).max(128), registrationId: z.uuid(),
  items: z.array(conditionalMultiMarketOrderHistoryItemSchema).max(50),
  nextCursor: conditionalMultiMarketOrderHistoryCursorSchema.nullable(), ...noAuthority,
}).transform((page, ctx) => {
  const last = page.items.at(-1);
  const ids = new Set(page.items.map(item => item.attemptId));
  const unordered = page.items.some((item, i) => {
    const prior = page.items[i - 1];
    return prior !== undefined && (item.admittedAt > prior.admittedAt
      || item.admittedAt === prior.admittedAt && item.attemptId >= prior.attemptId);
  });
  if (ids.size !== page.items.length || unordered || page.nextCursor !== null
    && (!last || page.nextCursor.admittedAt !== last.admittedAt || page.nextCursor.attemptId !== last.attemptId)) {
    ctx.addIssue({ code: 'custom', message: 'Inconsistent conditional order history page' });
  }
  return page;
});
export type ConditionalMultiMarketOrderHistoryPage = z.infer<typeof conditionalMultiMarketOrderHistoryPageSchema>;
